import {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "@tanstack/react-router";
import {
  getRedirectResult,
  fetchSignInMethodsForEmail,
  isSignInWithEmailLink,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
} from "firebase/auth";
import {useLocalStorage} from "@uidotdev/usehooks";
import {
  useAuthState,
  useSignInWithEmailAndPassword,
} from "react-firebase-hooks/auth";
import {useForm} from "@tanstack/react-form";
import {z} from "zod";
import * as Sentry from "@sentry/react";

import {auth} from "@/features/api";
import {useAuthWithProvider} from "@/features/auth/hooks/useAuthWithProvider";
import {useSignInWithEmailLink} from "@/features/auth/hooks/useSignInWithEmailLink";
import useSendSignInLinkToEmail from "@/features/auth/hooks/useSendSignInLinkToEmail";
import useSignByToken from "@/features/auth/hooks/useSignByToken";
import useRequestOTPCode from "@/features/auth/hooks/useRequestOTPCode";
import {isMobile} from "@/utils/detectors";
import Input from "@/components/Input";
import RippleButton from "@/components/RippleButton";
import useEffectOnce from "@/hooks/useEffectOnce";
import {sanitize} from "@/utils/sanitize";
import Spinner from "@/components/Spinner";
import ErrorBox from "@/components/ErrorBox";
import {AuthErrorMap, IgnoredAuthErrors} from "@/constants/errors";
import PasswordInput from "@/components/PasswordInput";
import {getUserClaims, UserClaims} from "@/features/api/lib/firebase";
import usePostReactivateUser from "@/features/auth/hooks/usePostReactivateUser";
import Config from "@/config";

import Facebook from "@/icons/facebook.svg?react";
import Google from "@/icons/google.svg?react";
import {logEvent} from "@/utils/analytics";
import {stripSearchParam, toSafeReturnPath} from "@/utils/url";
import useSignOut from "../../hooks/useSignOut";
import {getErrorText} from "@/utils/form";
import Countdown from "../../components/Countdown";
import {isAuthError} from "@/utils/typeGuards";
import {useSetLocalStorageMutation} from "@/hooks/useSetLocalStorageMutation";
import LastLoginLabel from "../../components/LastLoginLabel";
import OTPInputForm from "@/features/auth/components/OTPInputForm";
import {REACTIVATED_USER_TIMESTAMP_KEY} from "@/utils/localStorage";

// auth mode is redirect on mobile and popup on desktop
const authMode = isMobile ? "redirect" : "popup";

export const LoginPage = () => {
  const navigate = useNavigate();
  const postReactivateUser = usePostReactivateUser();

  // Local state hooks
  const [hasPassword, setHasPassword] = useState(false);
  const [isSignInWithLink, setIsSignInWithLink] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [errorText, setErrorText] = useState("");
  const [passwordErrorText, setPasswordErrorText] = useState("");
  const [emailErrorText, setEmailErrorText] = useState("");
  const [redirectError, setRedirectError] = useState("");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [isTryAgainButtonDisabled, setIsTryAgainButtonDisabled] =
    useState(true);

  // get email from URL
  const params = new URLSearchParams(document.location.search);
  const email = params.get("m");
  const [emailFromURL, setEmailFromURL] = useState(email || "");
  const [currentView, setCurrentView] = useState("buttons");

  // Local storage hooks
  const [previousPath, setPreviousPath] = useLocalStorage("previousPath", "");
  const [isAuthRedirect, setIsAuthRedirect] = useLocalStorage(
    "isAuthRedirect",
    false,
  );
  const [authEmail, setAuthEmail] = useLocalStorage("authEmail", "");
  const [lastLoginMethod, setLastLoginMethod] = useLocalStorage(
    "lastLoginMethod",
    "",
  );

  // Firebase Auth hooks
  const [user, isAuthStateLoading, authStateError] = useAuthState(auth);
  const signInWithFacebook = useAuthWithProvider(
    auth,
    "signIn",
    authMode,
    "facebook",
  );
  const signInWithGoogle = useAuthWithProvider(
    auth,
    "signIn",
    authMode,
    "google",
  );
  const [
    signInWithEmailAndPassword,
    ,
    isEmailPasswordLoading,
    emailPasswordError,
  ] = useSignInWithEmailAndPassword(auth);
  const [signInWithEmailLink, isEmailLinkLoading, emailLinkError] =
    useSignInWithEmailLink(auth);
  const sendSignInLinkToEmail = useSendSignInLinkToEmail();
  const requestOTPCode = useRequestOTPCode();
  const signByToken = useSignByToken();
  const signOut = useSignOut(auth);
  const setLocalStorageMutation = useSetLocalStorageMutation();

  const isEmailLoading =
    isEmailPasswordLoading ||
    isEmailLinkLoading ||
    sendSignInLinkToEmail.isPending;

  const setLastLogin = (method: string) => {
    setLastLoginMethod(method);
    logEvent("login_method", {method});
  };

  // hide the card if auth state is loading (checking if user is logged in)
  // or signInWithEmailLink is loading and authEmail is known (from local storage), without any user interaction,
  // redirect to feed page is without the card
  const isCardHidden =
    isAuthStateLoading ||
    // if user is logged in, hide the card
    (!!user &&
      !signByToken.isError &&
      !postReactivateUser.isError &&
      !emailFromURL) ||
    (isEmailLinkLoading && !isSignInWithLink);

  // reset auth states in local storage
  // Only clears two stored values, so it stays cheap enough for effects to
  // depend on directly.
  const resetAuthStates = useCallback(() => {
    setAuthEmail("");
    setIsAuthRedirect(false);
  }, [setAuthEmail, setIsAuthRedirect]);

  const resetEmailFromURL = () => {
    setEmailFromURL("");
    stripSearchParam("m");
  };

  // reset local storage flags related to age verification
  const resetLocalStorageFlags = () => {
    // reset isVerifiedAge flag in local storage,
    // so the user will be asked to verify age again if user flag verifiedAge from auth token is false
    setLocalStorageMutation.mutate({
      key: "isVerifiedAge",
      value: false,
    });

    // reset isYoungerThan15 flag in local storage
    setLocalStorageMutation.mutate({
      key: "isYoungerThan15",
      value: false,
    });
  };

  useEffectOnce(() => {
    // if auth mode from previous session is redirect, check for redirect result
    if (isAuthRedirect) {
      // wait for redirect result and handle errors
      getRedirectResult(auth).catch(setRedirectError);
    }

    // if user opened the link from email
    // Firebase SDK will parse the code from the link.
    else if (isSignInWithEmailLink(auth, window.location.href)) {
      // Get the email if available. This should be available if the user completes
      // the flow on the same device where they started it.
      if (authEmail) {
        // sign in with email and link
        signInWithEmailLink(authEmail, window.location.href);
      } else {
        setIsSignInWithLink(true);

        // User opened the link on a different device. To prevent session fixation
        // attacks, ask the user to provide the associated email again.
        setNote("Zadejte e-mail, který jste použili pro přihlášení.");
        setCurrentView("buttons");
        logEvent("missing_email");
      }
    }
  }, []);

  const navigateToNextPage = () => {
    // Constrained before use: localStorage is outside the app's trust boundary,
    // and `href` is parsed as a URL rather than matched as a route, so an
    // off-origin value must not reach the router.
    const safePath = previousPath ? toSafeReturnPath(previousPath) : null;

    // Cleared even when rejected, so a bad value is not retried on every login.
    if (previousPath) setPreviousPath("");

    if (safePath) {
      // `href` rather than `to`: the stored path is a full href that may carry
      // a fragment (/settings#odmeny), and `to` is treated as a pathname, so
      // the router would try to match "/settings#odmeny" as a route.
      navigate({href: safePath});
      return;
    }

    // navigate to feed page
    navigate({to: "/"});
  };

  const handleSignByToken = (userClaims: UserClaims) => {
    // if user is not established user, the user just registered,
    // (the user doesn't have answered onboarding questions (intro-sets) yet)
    // and signed in with Google popup or the user is coming from Google redirect (mobile)
    if (
      !userClaims.isEstablishedUser &&
      (signInWithGoogle.isSuccess || isAuthRedirect)
    ) {
      // sign-up the user with token on the backend to create user profile and intro-sets
      signByToken.mutate(
        {},
        {
          onSuccess: () => {
            navigateToNextPage();
          },
        },
      );
    } else {
      navigateToNextPage();
    }
  };

  // Values and helpers this effect calls when it fires, rather than triggers
  // for it. The effect must run only on an auth-state transition: depending on
  // these identities would re-enter the whole sign-in branch on every render,
  // re-firing reactivation and sign-by-token requests.
  const authFlowRef = useRef({
    emailFromURL,
    resetAuthStates,
    handleSignByToken,
    resetLocalStorageFlags,
    postReactivateUser,
  });
  useEffect(() => {
    authFlowRef.current = {
      emailFromURL,
      resetAuthStates,
      handleSignByToken,
      resetLocalStorageFlags,
      postReactivateUser,
    };
  });

  useEffect(() => {
    // wait for authentication state to finish loading before processing user state
    if (isAuthStateLoading) return;

    // don't proceed if sign in with Google popup is in progress
    if (signInWithGoogle.isPending) return;

    // if user is logged in
    if (user) {
      // if user is logged in and email is from URL, show message with sign out
      if (authFlowRef.current.emailFromURL) {
        setCurrentView("messageWithSignOut");
        authFlowRef.current.resetAuthStates();
        return;
      }

      getUserClaims().then((userClaims) => {
        // userClaims is null if user is not logged in
        if (!userClaims) {
          authFlowRef.current.resetAuthStates();
          return;
        }

        // if user is wanting to delete their account, but skip reactivation
        // for users flagged with a phone verification issue — their account
        // is held by the backend and must not be reactivated on login.
        if (
          userClaims.isPendingDeletion &&
          !userClaims.isPhoneVerificationIssue
        ) {
          // reactivate user
          authFlowRef.current.postReactivateUser.mutate(
            {},
            {
              onSuccess: () => {
                // save timestamp of reactivation in session storage for displaying message
                // for 2 hours
                window.localStorage.setItem(
                  REACTIVATED_USER_TIMESTAMP_KEY,
                  Date.now().toString(),
                );

                authFlowRef.current.resetAuthStates();
                authFlowRef.current.handleSignByToken(userClaims);
              },
            },
          );
        } else {
          authFlowRef.current.resetAuthStates();
          authFlowRef.current.handleSignByToken(userClaims);
        }
      });
    }

    // user is not logged in
    else {
      // reset local storage flags related to age verification
      authFlowRef.current.resetLocalStorageFlags();
    }
  }, [user, isAuthStateLoading, signInWithGoogle.isPending]);

  useEffect(() => {
    const error =
      authStateError ||
      signInWithFacebook.error ||
      signInWithGoogle.error ||
      emailPasswordError ||
      emailLinkError ||
      sendSignInLinkToEmail.error ||
      redirectError ||
      signByToken.error ||
      postReactivateUser.error ||
      setLocalStorageMutation.error;

    if (!error) {
      setErrorText("");
      setEmailErrorText("");
      setPasswordErrorText("");
      return;
    }

    // Reached through the ref rather than depended on: `resetAuthStates`
    // closes over the `useLocalStorage` setters, whose identity changes with
    // the stored value. Depending on it would make this effect re-run its own
    // write and report the same error twice.
    authFlowRef.current.resetAuthStates();

    const code = isAuthError(error)
      ? error.code
      : typeof error === "string"
        ? ""
        : (error.name ?? "");

    const message = isAuthError(error)
      ? error.message
      : typeof error === "string"
        ? error
        : (error.message ?? "");

    // ignore some errors
    if (IgnoredAuthErrors.includes(code)) return;

    // if the error is from cloud function blockNewUsers
    if (
      code === "auth/internal-error" &&
      message.includes("Unauthorized email domain")
    ) {
      // display the message instead of the error code
      // code = "auth/unauthorized-email-domain";
      displayMessageForDisabledRegistration();
      return;
    }

    // if user is signin with Google and registration is disabled
    if (
      Config.DISABLED_REGISTRATION &&
      code === "auth/admin-restricted-operation"
    ) {
      displayMessageForDisabledRegistration();
      return;
    }

    switch (code) {
      case "auth/wrong-password":
        setPasswordErrorText(AuthErrorMap[code]);
        break;
      case "auth/user-not-found":
        setEmailErrorText(AuthErrorMap[code]);
        break;
      // if user signs in with a trusted provider, then signs in with untrusted provider with the same email
      // https://firebase.google.com/docs/auth/users#verified_email_addresses
      case "auth/account-exists-with-different-credential":
        setMessage(`Jejej!
              ::BR::
              Přihlášení přes Facebook se nepovedlo. Přihlaste se prosím přes e-mail
              a přejděte na stránku Nastavení. Tam si prosím znovu nastavte
              propojení s Facebookem.
              ::BR::
              Děkujeme.`);
        setCurrentView("messageWithBack");
        break;
      default:
        setErrorText(
          `${AuthErrorMap[code as keyof typeof AuthErrorMap] || message || code || error}`,
        );
        break;
    }
  }, [
    authStateError,
    signInWithFacebook.error,
    signInWithGoogle.error,
    emailPasswordError,
    emailLinkError,
    sendSignInLinkToEmail.error,
    redirectError,
    signByToken.error,
    postReactivateUser.error,
    setLocalStorageMutation.error,
  ]);

  // Kept for backward compatibility - currently using OTP instead
  const sendLinkToEmail = async (email: string) => {
    const isSent = await sendSignInLinkToEmail.mutateAsync(email);

    if (isSent) {
      setMessage(
        `Na e-mail ::BS::${email}::BE::::BR::Vám byl poslán odkaz pro přihlášení.`,
      );
      setCurrentView("messageForSentEmail");

      // Save the email locally so you don't need to ask the user for it again
      // if they open the link on the same device.
      setAuthEmail(email);
    }
  };

  const displayMessageForDisabledRegistration = () => {
    Sentry.captureMessage(
      "Registration disabled - user attempted to register",
      "error",
    );
    setMessage(
      "Není možné se registrovat do TESTOVACÍHO prostředí. Správná adresa pro registraci je moje.trendaro.cz.",
    );
    setCurrentView("message");
  };

  // Check if user account exists by attempting to sign in with dummy password.
  // This is safe and won't create accounts or send emails.
  const checkUserExists = async (email: string): Promise<boolean> => {
    try {
      // Try to sign in with a dummy password using direct Firebase Auth function
      // This will NOT create an account, only attempt to sign in
      await firebaseSignInWithEmailAndPassword(
        auth,
        email,
        "dummy-password-that-wont-work",
      );
      // If this succeeds (very unlikely), user exists
      console.log("logged in with dummy password");
      return true;
    } catch (error) {
      if (isAuthError(error)) {
        console.log("error", error);
        switch (error.code) {
          case "auth/wrong-password":
            // If wrong password, user exists
            return true;
          case "auth/user-not-found":
            // If user not found, user doesn't exist
            return false;
          case "auth/too-many-requests":
            // If too many requests, assume user exists (safer)
            return true;
          default:
            // For any other auth error, assume user exists (safer)
            return true;
        }
      }
      // For any other error, assume user exists (safer)
      return true;
    }
  };

  const handleNextButton = async (event: {
    value: {email: string; password: string};
  }) => {
    const {email, password} = event.value;

    // reset error texts
    setErrorText("");
    setEmailErrorText("");
    setPasswordErrorText("");

    // remove email from URL, if any
    resetEmailFromURL();

    // if email link is opened in another window or device
    if (isSignInWithLink) {
      signInWithEmailLink(email, window.location.href);
      setLastLogin("email_link");
      return;
    }

    const signInMethodsForEmail = await fetchSignInMethodsForEmail(auth, email);

    // Check if user has password provider
    const hasPasswordProvider = signInMethodsForEmail.includes("password");

    // If registration is restricted to a specific domain, check email domain
    if (
      Config.RESTRICTED_REGISTRATION &&
      !email.includes(Config.RESTRICTED_REGISTRATION_DOMAIN)
    ) {
      // Check if user actually exists.
      const userExists = await checkUserExists(email);

      // For restricted registration, we only allow existing users or users with allowed domain
      if (!userExists) {
        displayMessageForDisabledRegistration();
        return;
      }
    }

    // if user has password and entered password, sign in with email and password
    if (hasPassword) {
      signInWithEmailAndPassword(email, password);
      setLastLogin("email_password");
    } else {
      // if user has password provider, ask for password in the next step
      if (hasPasswordProvider) {
        setHasPassword(true);
      } else {
        // If registration is disabled and user doesn't exist, show disabled message
        if (Config.DISABLED_REGISTRATION) {
          // Check if user actually exists.
          const userExists = await checkUserExists(email);

          if (!userExists) {
            displayMessageForDisabledRegistration();

            return;
          }
        }

        // Send OTP code directly
        try {
          const isSent = await requestOTPCode.mutateAsync(email);

          if (isSent) {
            setOtpEmail(email);
            setPendingEmail(email);
            setCurrentView("otpInput");
            setIsTryAgainButtonDisabled(true);
            setLastLogin("email_link");
            logEvent("otp_requested");
          }
        } catch (error) {
          let errorMessage = "Nepodařilo se odeslat OTP kód.";

          if (typeof error === "string") {
            errorMessage = error;
          } else if (error instanceof Error) {
            errorMessage = error.message || errorMessage;

            if (isAuthError(error)) {
              errorMessage =
                AuthErrorMap[error.code as keyof typeof AuthErrorMap] ||
                error.message ||
                errorMessage;
            }
          }

          setErrorText(errorMessage);
        }
      }
    }
  };

  const form = useForm({
    defaultValues: {
      email: emailFromURL,
      password: "",
    },
    onSubmit: handleNextButton,
  });

  const renderSignInWithEmail = () => {
    return (
      <div className="gap-g-2 flex w-full flex-col">
        {note && <div className="px-0.5">{note}</div>}
        <form
          className="gap-g-2 flex w-full flex-col items-center"
          spellCheck="false"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field
            name="email"
            validators={{
              onChange: z
                .string()
                .trim()
                .email("E-mail není ve správném formátu."),
            }}
            children={(field) => {
              return (
                <Input
                  label="E-mail"
                  type="email"
                  autoComplete="username"
                  errorText={getErrorText(field) || emailErrorText}
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              );
            }}
          />

          {hasPassword && (
            <form.Field
              name="password"
              validators={{
                onChange: z
                  .string()
                  .trim()
                  .min(3, "Heslo musí mít délku alespoň 3 znaky."),
              }}
              children={(field) => {
                return (
                  <PasswordInput
                    label="Heslo"
                    // https://developer.apple.com/documentation/security/enabling-password-autofill-on-an-html-input-element
                    autoComplete="current-password"
                    errorText={getErrorText(field) || passwordErrorText}
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                );
              }}
            />
          )}

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <RippleButton
                className="relative w-[240px] text-center"
                type="submit"
                variant="primary"
                centerText={true}
                disabled={!canSubmit}
                isPending={isSubmitting || isEmailLoading}
                disabledRippleEffect={true}
              >
                {["email_link", "email_password"].includes(lastLoginMethod) && (
                  <LastLoginLabel />
                )}
                <span>Pokračovat</span>
              </RippleButton>
            )}
          />
        </form>
      </div>
    );
  };

  const renderSignInButtons = () => {
    return (
      <div className="gap-g-2 flex flex-col items-center">
        <h1 className="mb-g-1 mt-g-1 text-3xl font-bold">
          Vstoupit do Trendara
        </h1>

        {renderSignInWithEmail()}

        <div className="before:border-outline after:border-outline mx-auto flex w-[220px] items-center py-3 text-sm before:me-6 before:flex-1 before:border-t after:ms-6 after:flex-1 after:border-t">
          nebo
        </div>

        <RippleButton
          className="relative w-[240px] justify-start"
          variant="white"
          isPending={signInWithGoogle.isPending}
          disabledRippleEffect={true}
          onClick={() => {
            if (authMode === "redirect") setIsAuthRedirect(true);
            if (emailFromURL) resetEmailFromURL();
            setLastLogin("google");
            signInWithGoogle.mutate({});
          }}
        >
          {lastLoginMethod === "google" && <LastLoginLabel />}
          <Google className="size-i-2" />
          Vstoupit přes Google
        </RippleButton>
        <RippleButton
          className="hide relative w-full justify-start"
          variant="facebook"
          isPending={signInWithFacebook.isPending}
          disabledRippleEffect={true}
          onClick={() => {
            if (authMode === "redirect") setIsAuthRedirect(true);
            setLastLogin("facebook");
            signInWithFacebook.mutate({});
          }}
        >
          {lastLoginMethod === "facebook" && <LastLoginLabel />}
          <Facebook className="size-i-2" />
          Vstoupit přes Facebook
        </RippleButton>
      </div>
    );
  };

  const renderMessage = () => {
    return (
      <>
        <h1 className="mb-g-1 mt-g-1 text-3xl font-bold">
          Vstoupit do Trendara
        </h1>
        <div className="px-0.5">{sanitize(message)}</div>
      </>
    );
  };

  const renderMessageWithBack = () => {
    return (
      <div className="gap-g-3 flex flex-col items-center text-center">
        <h1 className="mb-g-1 mt-g-1 text-3xl font-bold">
          Vstoupit do Trendara
        </h1>
        <div>{sanitize(message)}</div>
        <RippleButton
          className="mt-g-2"
          variant="primary"
          onClick={() => {
            setCurrentView("buttons");
          }}
        >
          Zpět
        </RippleButton>
      </div>
    );
  };

  const renderMessageWithSignOut = () => {
    return (
      <div className="gap-g-3 flex flex-col items-center text-center">
        <h1 className="mb-g-1 mt-g-1 text-3xl font-bold">
          Vstoupit do Trendara
        </h1>
        <div>
          Jste přihlášeni jako <b>{user?.email}</b>. <br />
          Chcete se odhlásit a přihlásit jako <b>{emailFromURL}</b>?
        </div>
        <div className="gap-g-2 flex flex-row">
          <RippleButton
            className="mt-g-2"
            variant="secondary"
            onClick={() => {
              navigate({to: "/"});
            }}
          >
            Zpět do aplikace
          </RippleButton>
          <RippleButton
            className="mt-g-2"
            variant="primary"
            onClick={() => {
              signOut.mutate();
              resetAuthStates();
              setCurrentView("buttons");
            }}
          >
            Odhlásit se
          </RippleButton>
        </div>
      </div>
    );
  };

  const renderMessageForSentEmail = () => {
    return (
      <div className="gap-g-3 flex flex-col items-center text-center">
        <h1 className="mb-g-1 mt-g-1 text-3xl font-bold">E-mail odeslán</h1>
        <div>{sanitize(message)}</div>
        <RippleButton
          className="mt-g-2"
          variant="outlined"
          onClick={() => {
            setCurrentView("guideForEmail");

            // reset the button disabled state
            setIsTryAgainButtonDisabled(true);
          }}
        >
          Nepřišel vám email?
        </RippleButton>
      </div>
    );
  };

  const renderGuideForEmail = () => {
    return (
      <div className="gap-g-3 flex flex-col items-center">
        <h1 className="mb-g-1 mt-g-1 text-3xl font-bold">
          Nepřišel Vám e-mail?
        </h1>
        <div className="w-full">
          Vyzkoušejte tato běžná řešení:
          <br /> <br />
          <ul className="pl-g-3 list-disc">
            <li>
              Zkontrolujte, jestli e-mail nebyl označen jako spam nebo nebyl
              odstraněn jiným filtrem.
            </li>
            <li>Zkontrolujte připojení k internetu.</li>
            <li>
              Zkontrolujte, zda jste adresu e-mailu <b>{authEmail}</b> napsali
              správně.
            </li>
            <li>
              Zkontrolujte, jestli nemáte plnou schránku příchozích zpráv nebo
              nedošlo k nějakému jinému problému se schránkou.
            </li>
          </ul>
          <br />
          Pokud žádné z uvedených řešení nepomohlo, můžete si e-mail nechat
          zaslat znovu. Odkaz v prvním e-mailu pak bude deaktivován.
        </div>
        <div className="gap-g-2 flex w-full flex-row">
          <RippleButton
            className="mt-g-2"
            variant="secondary"
            onClick={() => {
              setCurrentView("buttons");
            }}
          >
            Zpět
          </RippleButton>
          <div className="grow"></div>
          <RippleButton
            className="mt-g-2"
            variant="primary"
            disabled={isTryAgainButtonDisabled}
            onClick={() => {
              setCurrentView("buttons");
            }}
          >
            {isTryAgainButtonDisabled ? (
              <>
                Zkusit znovu za{" "}
                <Countdown
                  seconds={30}
                  onEnd={() => setIsTryAgainButtonDisabled(false)}
                />
                s
              </>
            ) : (
              "Zkusit znovu"
            )}
          </RippleButton>
        </div>
      </div>
    );
  };

  const handleOtpBack = () => {
    setOtpEmail("");
    setPendingEmail("");
    setCurrentView("buttons");
  };

  const renderOtpInput = () => (
    <OTPInputForm
      otpEmail={otpEmail}
      fallbackEmail={pendingEmail || otpEmail}
      onBack={handleOtpBack}
      isTryAgainButtonDisabled={isTryAgainButtonDisabled}
      setIsTryAgainButtonDisabled={setIsTryAgainButtonDisabled}
      onSendLink={sendLinkToEmail}
      isSendLinkPending={sendSignInLinkToEmail.isPending}
    />
  );

  const views = {
    buttons: renderSignInButtons,
    message: renderMessage,
    messageWithBack: renderMessageWithBack,
    messageWithSignOut: renderMessageWithSignOut,
    messageForSentEmail: renderMessageForSentEmail,
    guideForEmail: renderGuideForEmail,
    otpInput: renderOtpInput,
  };

  return (
    <>
      {!isCardHidden && (
        <div className="flex flex-1">
          <div className="gap-g-2 bg-surface-bright text-on-surface fill-on-surface p-g-2 sm:p-g-3 relative m-auto flex w-96 flex-col items-center justify-center rounded-md">
            <div className="size-[150px] rounded-full bg-[url('/images/pig.jpg')] bg-cover bg-no-repeat"></div>

            {views[currentView as keyof typeof views]()}
            <ErrorBox error={errorText} displayTime={10000} />
          </div>
        </div>
      )}
      {isCardHidden && <Spinner />}
    </>
  );
};
